import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  InputLabel,
  TableRow,
  MenuItem,
  TablePagination,
  FormControl,
  TextField,
  IconButton,
  Select,
  Box,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import Swal from 'sweetalert2';
import { gridSpacing } from 'config.js';

// API URLs for Department
const apiUrl = 'https://apidashboard.eurekagroup.id/Department';
const addDepartmentUrl = `${apiUrl}/add`;
const editDepartmentUrl = `${apiUrl}/edit`;
const deleteDepartmentUrl = (id) => `${apiUrl}/delete/${id}`;

const DepartmentPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successPopup, setSuccessPopup] = useState(false);
  const [message, setMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentCode, setDepartmentCode] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState({
    departmentCode: false,
    departmentName: false,
  });

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoading(true);
        const response = await axios.get(apiUrl);
        setData(response.data);
      } catch (error) {
        console.error('Error fetching departments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDepartments();
  }, []);

  const handleSearchChange = (event) => setSearchText(event.target.value);
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (department) => {
    if (department) {
      setSelectedDepartment(department);
      setDepartmentCode(department.department_code);
      setDepartmentName(department.department_name);
      setIsActive(department.is_active);
    } else {
      setSelectedDepartment(null);
      setDepartmentCode('');
      setDepartmentName('');
      setIsActive(true);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleSaveDepartment = async () => {
    const departmentData = { department_code: departmentCode, department_name: departmentName, is_active: isActive };
    let formIsValid = true;
    const newErrors = { ...errors };
  
    // Validasi input form
    if (!departmentCode) {
      newErrors.departmentCode = true;
      formIsValid = false;
    } else {
      newErrors.departmentCode = false;
    }
  
    if (!departmentName) {
      newErrors.departmentName = true;
      formIsValid = false;
    } else {
      newErrors.departmentName = false;
    }
  
    setErrors(newErrors);
  
    if (!formIsValid) {
      return; // Jangan lanjutkan jika form tidak valid
    }
  
    const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
    if (selectedDepartment) {
      // Jika mengedit department
      try {
        await axios.put(
          editDepartmentUrl,
          { ...departmentData, id: selectedDepartment._id },
          {
            headers: {
              Authorization: `Bearer ${token}`, // Tambahkan header Authorization
            },
          }
        );
        setData(data.map(dept => (dept._id === selectedDepartment._id ? { ...dept, ...departmentData } : dept)));
        setMessage('Department updated successfully');
      } catch (error) {
        console.error('Error updating department:', error.response?.data || error.message);
        Swal.fire({
          title: 'Error',
          text: error.response?.data?.error || 'Failed to update department',
          icon: 'error',
          confirmButtonText: 'Okay',
        });
      }
    } else {
      // Jika menambahkan department baru
      try {
        const response = await axios.post(addDepartmentUrl, departmentData, {
          headers: {
            Authorization: `Bearer ${token}`, // Tambahkan header Authorization
          },
        });
        setData([...data, response.data]);
        setMessage('Department added successfully');
      } catch (error) {
        console.error('Error adding department:', error.response?.data || error.message);
        Swal.fire({
          title: 'Error',
          text: error.response?.data?.error || 'Failed to add department',
          icon: 'error',
          confirmButtonText: 'Okay',
        });
      }
    }
  
    setSuccessPopup(true);
    handleCloseDialog();
  };  

  const handleDeleteClick = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });
  
    if (result.isConfirmed) {
      const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
      try {
        await axios.delete(deleteDepartmentUrl(id), {
          headers: {
            Authorization: `Bearer ${token}`, // Tambahkan header Authorization
          },
        }); // Panggil API DELETE dengan token
        setData(data.filter(dept => dept._id !== id));
        setMessage('Department deleted successfully');
        setSuccessPopup(true);
      } catch (error) {
        console.error('Error deleting department:', error.response?.data || error.message);
        Swal.fire({
          title: 'Error',
          text: error.response?.data?.error || 'Failed to delete department',
          icon: 'error',
          confirmButtonText: 'Okay',
        });
      }
    }
  };  

  const filteredData = data.filter(dept =>
    dept.department_name.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <>
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <BusinessIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Manajemen Departemen
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola data departemen dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
        action={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<RefreshIcon />} 
              onClick={() => window.location.reload()}
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />} 
              onClick={() => handleOpenDialog(null)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              ✨ Tambah Departemen
            </Button>
          </Box>
        }
        sx={{ pb: 2 }}
      />
      <Divider />
      <CardContent>
        {/* Filter Section */}
        <Box sx={{ 
          mb: 3, 
          p: 3, 
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200'
        }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cari Departemen"
                variant="outlined"
                value={searchText}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {filteredData.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Departemen
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Table Section */}
        <Box sx={{ 
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 400,
              flexDirection: 'column',
              gap: 2
            }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Memuat data departemen...
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        No
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Kode Departemen
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Nama Departemen
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textAlign: 'center'
                      }}>
                        Aksi
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((dept, index) => (
                      <TableRow 
                        key={dept._id}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                            transform: 'scale(1.001)',
                            transition: 'all 0.2s ease'
                          },
                          '&:nth-of-type(even)': {
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {index + 1 + page * rowsPerPage}
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {dept.department_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {dept.department_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ 
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: dept.is_active ? 'success.light' : 'error.light',
                            color: dept.is_active ? 'success.dark' : 'error.dark',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {dept.is_active ? 'Aktif' : 'Tidak Aktif'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <IconButton 
                              onClick={() => handleOpenDialog(dept)} 
                              sx={{ 
                                color: 'info.main',
                                '&:hover': { 
                                  bgcolor: 'info.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              onClick={() => handleDeleteClick(dept._id)} 
                              sx={{ 
                                color: 'error.main',
                                '&:hover': { 
                                  bgcolor: 'error.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'grey.200',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                }}
              />
            </>
          )}
        </Box>
      </CardContent>
    </Card>
    </div>

      {/* Success Snackbar */}
      <Snackbar
        open={successPopup}
        autoHideDuration={3000}
        onClose={() => setSuccessPopup(false)}
        message={message}
      />

      {/* Add/Edit Department Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{selectedDepartment ? 'Edit Department' : 'Add Department'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
          <Grid item xs={12}>
          <TextField
            label="Department Code"
            variant="outlined"
            fullWidth
            value={departmentCode}
            onChange={(e) => setDepartmentCode(e.target.value)}
            error={errors.departmentCode}
            helperText={errors.departmentCode ? 'Department code is required' : ''}
            margin="normal" // Add this to move the label down slightly
          />
        </Grid>
            <Grid item xs={12}>
              <TextField
                label="Department Name"
                variant="outlined"
                fullWidth
                value={departmentName}
                onChange={(e) => setDepartmentName(e.target.value)}
                error={errors.departmentName}
                helperText={errors.departmentName ? 'Department name is required' : ''}
              />
            </Grid>
            <Grid item xs={12}>
            <FormControl margin="dense" error={isActive === ''}>
              <InputLabel id="is-active-label">Status</InputLabel>
              <Select
                labelId="is-active-label"
                value={isActive}
                onChange={(e) => setIsActive(e.target.value)}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </Select>
              {isActive === '' && <FormHelperText>Status is required</FormHelperText>} 
            </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveDepartment} variant="contained" color="primary">
            {selectedDepartment ? 'Save Changes' : 'Add Department'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DepartmentPage;
